import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import BigNumber from 'bignumber.js';
import { CircleHelp, Dot, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { useCalculator } from '../context';

export interface IEmergencyContentProps {
  addFactorSixtyToCalculate: boolean;
  addFactorThousandToCalculate: boolean;
  addWeightToCalculate: boolean;
  ampouleDefault?: number;
  applicationUnitMeasure?: string;
  concentrationUnitMeasure?: string;
  currentFlowDefault?: number;
  diluteDefault?: number;
  doseDecimalPlaces?: number;
  doseDefault?: number;
  doseUnitMeasure: string;
  drugDefault: string;
  drugOptions: {
    label: string;
    value: string;
  }[];
  maxDoseDefault?: number;
  minDoseDefault?: number;
  observations?: string[];
  salineDefault?: number;
  salineLabel?: string;
  suggestions?: string[];
  templateJustDoseAndApplication?: boolean;
  templateJustDoseAndApplication2?: boolean;
  title: string;
}

const formSchema = z.object({
  drug: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ampoule: z.string().min(1, {
    message: errorsValidation.required,
  }),
  sg: z.string().min(1, {
    message: errorsValidation.required,
  }),
  minDose: z.string().min(1, {
    message: errorsValidation.required,
  }),
  maxDose: z.string().min(1, {
    message: errorsValidation.required,
  }),
  currentFlow: z.string().min(1, {
    message: errorsValidation.required,
  }),
  dose: z.string().min(1, {
    message: errorsValidation.required,
  }),
  dilute: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function Content({
  addFactorSixtyToCalculate,
  addFactorThousandToCalculate,
  addWeightToCalculate,
  ampouleDefault = 0,
  applicationUnitMeasure = '',
  concentrationUnitMeasure = '',
  currentFlowDefault = 0,
  diluteDefault = 0,
  doseDecimalPlaces = 1,
  doseDefault = 0,
  doseUnitMeasure,
  drugDefault,
  drugOptions,
  maxDoseDefault = 0,
  minDoseDefault = 0,
  observations = [],
  salineDefault = 0,
  salineLabel = '',
  suggestions = [],
  templateJustDoseAndApplication = false,
  templateJustDoseAndApplication2 = false,
  title,
}: IEmergencyContentProps) {
  const { weight } = useCalculator();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      drug: drugDefault,
      ampoule: String(ampouleDefault),
      sg: String(salineDefault),
      minDose: minDoseDefault.toFixed(doseDecimalPlaces),
      maxDose: maxDoseDefault.toFixed(doseDecimalPlaces),
      currentFlow: currentFlowDefault.toFixed(1),
      dose: doseDefault.toFixed(1),
      dilute: diluteDefault.toFixed(1),
    },
  });

  const [selectedDrug, setSelectedDrug] = useState<string>(drugDefault);

  const [ampoule, setAmpoule] = useState<number>(ampouleDefault);
  const [saline, setSaline] = useState<number>(salineDefault);
  const [maxDose, setMaxDose] = useState<number>(maxDoseDefault);
  const [minDose, setMinDose] = useState<number>(minDoseDefault);
  const [currentFlow, setCurrentFlow] = useState<number>(currentFlowDefault);
  const [dose, setDose] = useState<number>(doseDefault);
  const [dilute, setDilute] = useState<number>(diluteDefault);

  const [concentration, setConcentration] = useState<number>(0.06);
  const [maxFlow, setMaxFlow] = useState<number>(2);
  const [minFlow, setMinFlow] = useState<number>(2);
  const [currentDose, setCurrentDose] = useState<number>(0);
  const [applicationValue, setApplicationValue] = useState<number>(0);

  const getDrug = useCallback((): { mg: number; ml: number } => {
    if (!selectedDrug) {
      return {
        mg: 0,
        ml: 0,
      };
    }
    const drug = JSON.parse(selectedDrug) as { mg: number; ml: number };
    return drug;
  }, [selectedDrug]);

  const handleDrugSelect = useCallback((drug: string): void => {
    setSelectedDrug(drug);
  }, []);

  const handleChange = useCallback(
    ({
      set,
      value,
    }: {
      set: Dispatch<SetStateAction<number>>;
      value: string;
    }): void => {
      if (!value) {
        set(0);
      }
      const valueParsed = Number(value.replace(',', '.'));
      set(valueParsed);
    },
    [],
  );

  const calculateConcentration = useCallback((): void => {
    const drug = getDrug();
    const drugMgParsed = BigNumber(drug.mg);
    const ampouleParsed = BigNumber(ampoule);
    const salineParsed = BigNumber(saline);
    const drugMlParsed = BigNumber(drug.ml);
    const concentrationCalculated = drugMgParsed
      .multipliedBy(ampouleParsed)
      .dividedBy(drugMlParsed.multipliedBy(ampoule).plus(salineParsed));
    setConcentration(concentrationCalculated.toNumber());
  }, [ampoule, getDrug, saline]);

  const calculateMinFlow = useCallback((): void => {
    const minDoseParsed = BigNumber(minDose);
    const weightParsed = BigNumber(weight);
    const concentrationParsed = BigNumber(concentration);
    const minFlowCalculated = minDoseParsed
      .multipliedBy(BigNumber(addWeightToCalculate ? weightParsed : 1))
      .multipliedBy(BigNumber(addFactorSixtyToCalculate ? 60 : 1))
      .dividedBy(
        concentrationParsed.multipliedBy(
          BigNumber(addFactorThousandToCalculate ? 1000 : 1),
        ),
      );
    setMinFlow(minFlowCalculated.toNumber());
  }, [
    minDose,
    weight,
    concentration,
    addWeightToCalculate,
    addFactorSixtyToCalculate,
    addFactorThousandToCalculate,
  ]);

  const calculateMaxFlow = useCallback((): void => {
    const maxDoseParsed = BigNumber(maxDose);
    const weightParsed = BigNumber(weight);
    const concentrationParsed = BigNumber(concentration);
    const maxFlowCalculated = maxDoseParsed
      .multipliedBy(BigNumber(addWeightToCalculate ? weightParsed : 1))
      .multipliedBy(BigNumber(addFactorSixtyToCalculate ? 60 : 1))
      .dividedBy(
        concentrationParsed.multipliedBy(
          BigNumber(addFactorThousandToCalculate ? 1000 : 1),
        ),
      );
    setMaxFlow(maxFlowCalculated.toNumber());
  }, [
    addFactorSixtyToCalculate,
    addFactorThousandToCalculate,
    addWeightToCalculate,
    concentration,
    maxDose,
    weight,
  ]);

  const calculateCurrentDose = useCallback((): void => {
    const currentFlowParsed = BigNumber(currentFlow);
    const concentrationParsed = BigNumber(concentration);
    const weightParsed = BigNumber(weight);
    const currentDoseCalculated = concentrationParsed
      .multipliedBy(BigNumber(addFactorThousandToCalculate ? 1000 : 1))
      .dividedBy(
        BigNumber(addFactorSixtyToCalculate ? 60 : 1).multipliedBy(
          BigNumber(addWeightToCalculate ? weightParsed : 1),
        ),
      )
      .multipliedBy(currentFlowParsed);
    setCurrentDose(currentDoseCalculated.toNumber());
  }, [
    addFactorSixtyToCalculate,
    addFactorThousandToCalculate,
    addWeightToCalculate,
    concentration,
    currentFlow,
    weight,
  ]);

  const calculateApplication = useCallback((): void => {
    const drug = getDrug();
    const drugMgParsed = BigNumber(drug.mg);
    const drugMlParsed = BigNumber(
      templateJustDoseAndApplication2 ? dilute : drug.ml,
    );
    const weightParsed = BigNumber(weight);
    const applicationValueCalculated = BigNumber(dose)
      .multipliedBy(weightParsed)
      .dividedBy(BigNumber(drugMgParsed.dividedBy(drugMlParsed)));
    setApplicationValue(applicationValueCalculated.toNumber());
  }, [dilute, dose, getDrug, templateJustDoseAndApplication2, weight]);

  useEffect(() => {
    calculateConcentration();
  }, [calculateConcentration]);

  useEffect(() => {
    calculateMinFlow();
  }, [calculateMinFlow]);

  useEffect(() => {
    calculateMaxFlow();
  }, [calculateMaxFlow]);

  useEffect(() => {
    calculateCurrentDose();
  }, [calculateCurrentDose]);

  useEffect(() => {
    calculateApplication();
  }, [calculateApplication]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'ampoule' && value.ampoule) {
        handleChange({
          set: setAmpoule,
          value: value.ampoule,
        });
      }
      if (name === 'drug' && value.drug) {
        handleDrugSelect(value.drug);
      }
      if (name === 'sg' && value.sg) {
        handleChange({
          set: setSaline,
          value: value.sg,
        });
      }
      if (name === 'minDose' && value.minDose) {
        handleChange({
          set: setMinDose,
          value: value.minDose,
        });
      }
      if (name === 'maxDose' && value.maxDose) {
        handleChange({
          set: setMaxDose,
          value: value.maxDose,
        });
      }
      if (name === 'currentFlow' && value.currentFlow) {
        handleChange({
          set: setCurrentFlow,
          value: value.currentFlow,
        });
      }
      if (name === 'dose' && value.dose) {
        handleChange({
          set: setDose,
          value: value.dose,
        });
      }
      if (name === 'dilute' && value.dilute) {
        handleChange({
          set: setDilute,
          value: value.dilute,
        });
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  function onSubmit(): void {
    //
  }

  return (
    <Accordion className="mx-auto max-w-3xl" collapsible type="single">
      <AccordionItem value="item-1">
        <AccordionTrigger className="cursor-pointer text-lg uppercase hover:opacity-70">
          {title}
        </AccordionTrigger>
        <AccordionContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="mb-4 flex flex-row justify-center gap-2">
                <FormField
                  control={form.control}
                  name="drug"
                  render={({ field }) => (
                    <FormItem className="grid grid-cols-[auto_1fr] items-center gap-2">
                      <FormLabel className="text-tertiary">{title}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}>
                        <FormControl>
                          <SelectTrigger onBlur={field.onBlur}>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>

                        <SelectContent>
                          {drugOptions.map((drugOption) => (
                            <SelectItem
                              className="focus:bg-tertiary"
                              key={drugOption.value}
                              value={drugOption.value}>
                              {drugOption.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                {(suggestions.length > 0 || observations.length > 0) && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="cursor-pointer"
                        size="icon"
                        variant="ghost">
                        <CircleHelp
                          className="text-foreground"
                          strokeWidth={2}
                        />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 space-y-4">
                      <h1 className="text-secondary text-center font-medium">
                        {title}
                      </h1>
                      {suggestions.length > 0 && (
                        <div>
                          <Label className="text-tertiary">Sugestão</Label>
                          {suggestions.map((suggestion, index) => {
                            const hideDot = suggestion.startsWith('_');
                            const suggestionFormatted = suggestion.replace(
                              '_',
                              '',
                            );
                            return (
                              <div className="flex flex-row gap-1" key={index}>
                                {!hideDot && (
                                  <Dot className="text-foreground" />
                                )}
                                <p className="text-foreground flex-1 text-sm">
                                  {suggestionFormatted}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {observations.length > 0 && (
                        <div>
                          <Label className="text-tertiary">Observação</Label>
                          {observations.map((observation, index) => (
                            <div className="flex flex-row gap-1" key={index}>
                              <Dot className="text-foreground" />
                              <p className="text-foreground flex-1 text-sm">
                                {observation}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              {!templateJustDoseAndApplication &&
                !templateJustDoseAndApplication2 && (
                  <div>
                    <div className="mb-4 flex flex-row items-end justify-center gap-4">
                      <FormField
                        control={form.control}
                        name="ampoule"
                        render={({ field: { ref, ...rest } }) => (
                          <FormItem>
                            <FormLabel>Diluir (ampola)</FormLabel>
                            <FormControl>
                              <NumericFormat
                                className="[&_input]:w-26 [&_input]:text-center"
                                customInput={Input}
                                getInputRef={ref}
                                {...rest}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Plus
                        className="text-foreground mb-2"
                        size={24}
                        strokeWidth={2}
                      />
                      <FormField
                        control={form.control}
                        name="sg"
                        render={({ field: { ref, ...rest } }) => (
                          <FormItem>
                            <FormLabel>{salineLabel}</FormLabel>
                            <FormControl>
                              <NumericFormat
                                className="[&_input]:w-26 [&_input]:text-center"
                                customInput={Input}
                                getInputRef={ref}
                                {...rest}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-center">
                      <p className="text-sm">
                        Concentração{' '}
                        <span className="text-tertiary font-medium">
                          {concentration.toFixed(2)}
                        </span>{' '}
                        {concentrationUnitMeasure}
                      </p>
                    </div>

                    <Separator className="my-4" />

                    <Label className="text-tertiary text-sm font-light">
                      QUER SABER A VAZÃO?
                    </Label>

                    <div className="mb-4 mt-2 flex flex-row items-end justify-center gap-8">
                      <FormField
                        control={form.control}
                        name="minDose"
                        render={({ field: { ref, ...rest } }) => (
                          <FormItem>
                            <FormLabel>Dose mín</FormLabel>
                            <FormControl>
                              <NumberFormatBase
                                className="[&_input]:w-26 [&_input]:text-center"
                                customInput={Input}
                                format={(value) =>
                                  formatterDecimal(value, doseDecimalPlaces)
                                }
                                getCaretBoundary={(formattedValue) => {
                                  const boundaryAry = Array.from({
                                    length: formattedValue.length + 1,
                                  }).map(() => true);
                                  boundaryAry[0] = false;
                                  boundaryAry[1] = false;
                                  boundaryAry[2] = false;
                                  return boundaryAry;
                                }}
                                getInputRef={ref}
                                {...rest}
                              />
                            </FormControl>
                            <FormDescription className="font-light">
                              {doseUnitMeasure}
                            </FormDescription>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maxDose"
                        render={({ field: { ref, ...rest } }) => (
                          <FormItem>
                            <FormLabel>Dose máx</FormLabel>
                            <FormControl>
                              <NumberFormatBase
                                className="[&_input]:w-26 [&_input]:text-center"
                                customInput={Input}
                                format={(value) =>
                                  formatterDecimal(value, doseDecimalPlaces)
                                }
                                getCaretBoundary={(formattedValue) => {
                                  const boundaryAry = Array.from({
                                    length: formattedValue.length + 1,
                                  }).map(() => true);
                                  boundaryAry[0] = false;
                                  boundaryAry[1] = false;
                                  boundaryAry[2] = false;
                                  return boundaryAry;
                                }}
                                getInputRef={ref}
                                {...rest}
                              />
                            </FormControl>
                            <FormDescription className="font-light">
                              {doseUnitMeasure}
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex flex-row justify-center gap-8">
                      <div>
                        <Label>Vazão mín</Label>
                        <p className="font-light">
                          <span className="text-tertiary text-lg font-bold">
                            {minFlow.toFixed(1)}
                          </span>{' '}
                          mL/h
                        </p>
                      </div>

                      <div>
                        <Label>Vazão máx</Label>
                        <p className="font-light">
                          <span className="text-tertiary text-lg font-bold">
                            {maxFlow.toFixed(1)}
                          </span>{' '}
                          mL/h
                        </p>
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <Label className="text-tertiary text-sm font-light">
                      QUER SABER A DOSE ATUAL?
                    </Label>

                    <div className="mb-4 mt-2 flex flex-row items-end justify-center gap-8">
                      <FormField
                        control={form.control}
                        name="currentFlow"
                        render={({ field: { ref, ...rest } }) => (
                          <FormItem>
                            <FormLabel>Vazão atual</FormLabel>
                            <FormControl>
                              <NumberFormatBase
                                className="[&_input]:w-26 [&_input]:text-center"
                                customInput={Input}
                                format={(value) =>
                                  formatterDecimal(value, doseDecimalPlaces)
                                }
                                getCaretBoundary={(formattedValue) => {
                                  const boundaryAry = Array.from({
                                    length: formattedValue.length + 1,
                                  }).map(() => true);
                                  boundaryAry[0] = false;
                                  boundaryAry[1] = false;
                                  boundaryAry[2] = false;
                                  return boundaryAry;
                                }}
                                getInputRef={ref}
                                {...rest}
                              />
                            </FormControl>
                            <FormDescription className="font-light">
                              mL/h
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                      <div className="flex flex-col items-center">
                        <Label>Dose atual</Label>
                        <p className="my-3 font-light">
                          <span className="text-tertiary text-lg font-bold">
                            {currentDose.toFixed(1)}
                          </span>{' '}
                        </p>
                        <Label className="text-foreground text-sm font-light">
                          {doseUnitMeasure}
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

              {templateJustDoseAndApplication && (
                <div className="mb-4 flex flex-col items-center gap-4">
                  <FormField
                    control={form.control}
                    name="dose"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem className="flex flex-row items-center">
                        <FormLabel>Dose</FormLabel>
                        <FormControl>
                          <NumberFormatBase
                            className="[&_input]:w-18 [&_input]:text-center"
                            customInput={Input}
                            format={(value) =>
                              formatterDecimal(value, doseDecimalPlaces)
                            }
                            getCaretBoundary={(formattedValue) => {
                              const boundaryAry = Array.from({
                                length: formattedValue.length + 1,
                              }).map(() => true);
                              boundaryAry[0] = false;
                              boundaryAry[1] = false;
                              boundaryAry[2] = false;
                              return boundaryAry;
                            }}
                            getInputRef={ref}
                            {...rest}
                          />
                        </FormControl>
                        <FormDescription className="font-light">
                          {doseUnitMeasure}
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <div className="flex flex-row items-center gap-2">
                    <Label>Aplicar</Label>
                    <p className="my-3 font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {applicationValue.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      {applicationUnitMeasure}
                    </Label>
                  </div>
                </div>
              )}

              {templateJustDoseAndApplication2 && (
                <div>
                  <div className="mb-4 mt-2 flex flex-col items-center gap-4">
                    <FormField
                      control={form.control}
                      name="dose"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="flex flex-row items-center">
                          <FormLabel>Dose</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-26 [&_input]:text-center"
                              customInput={Input}
                              format={(value) =>
                                formatterDecimal(value, doseDecimalPlaces)
                              }
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            {doseUnitMeasure}
                          </FormDescription>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dilute"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="flex flex-row items-center">
                          <FormLabel>Diluir</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-26 [&_input]:text-center"
                              customInput={Input}
                              format={(value) =>
                                formatterDecimal(value, doseDecimalPlaces)
                              }
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            mL de SF 0,9%
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex flex-row items-center justify-center gap-2">
                    <Label>Aplicar</Label>
                    <p className="my-3 font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {applicationValue.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      {applicationUnitMeasure}
                    </Label>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default Content;
